import { Callbacks, Client, type Room } from "@colyseus/sdk";

/**
 * Thin adapter over `@colyseus/sdk` 0.18 so `RealtimeClient` depends on a small interface that a
 * unit test can fake. Everything SDK-specific (auth token, joinOrCreate, reconnect, schema
 * callbacks) lives here and follows ADR-0002.
 */

/** Public actor fields the realtime server keeps in `RoomState.actors` (brief section 4). */
export interface ActorStateLike {
  actorId: string;
  label: string;
  /** "" when hidden / unknown. */
  countryCode: string;
  kind: string;
  x: number;
  z: number;
  facing: number;
  anim: number;
  lastSeq: number;
  /** "male" | "female" (ADR-0004 #12); missing / "" on servers older than world v2. */
  bodyType?: string;
  /** Shirt colour (kept under its original name). */
  bodyColor: string;
  skinTone: string;
  hairStyle: string;
  /** Missing / "" on servers older than world v2. */
  hairColor?: string;
  outfit: string;
  /** The car this actor drives ("" or missing while walking; ADR-0006, contracts vehicles.ts). */
  vehicleId?: string;
  /** 0 standing, 1 sitting, 2 lying (ADR-0018); missing on older servers. */
  posture?: number;
  /** The seat while sitting / lying ("" or missing while standing). */
  seatId?: string;
}

/** Public fields of a shared town car in `RoomState.vehicles` (street rooms; contracts vehicles.ts). */
export interface VehicleStateLike {
  vehicleId: string;
  style: number;
  color: number;
  /** "traffic" | "driven" | "parked". */
  mode: string;
  x: number;
  z: number;
  heading: number;
  speed: number;
  /** "" unless driven. */
  driverActorId: string;
  /** Last InputDrive the server applied for this car (drive prediction acknowledgement). */
  lastSeq: number;
}

export interface VehicleHandlers {
  onAdd(vehicle: VehicleStateLike, key: string): void;
  onChange(vehicle: VehicleStateLike, key: string): void;
  onRemove(vehicle: VehicleStateLike, key: string): void;
}

export interface ActorHandlers {
  onAdd(actor: ActorStateLike, sessionId: string): void;
  onChange(actor: ActorStateLike, sessionId: string): void;
  onRemove(actor: ActorStateLike, sessionId: string): void;
}

export interface RoomHandle {
  readonly roomId: string;
  readonly sessionId: string;
  readonly reconnectionToken: string;
  onMessage(type: string, cb: (payload: unknown) => void): () => void;
  onLeave(cb: (code: number, reason?: string) => void): () => void;
  onError(cb: (code: number, message?: string) => void): () => void;
  /** Transport dropped; the SDK is retrying with the reconnection token. */
  onDrop(cb: (code: number, reason?: string) => void): () => void;
  onReconnect(cb: () => void): () => void;
  send(type: string, payload: unknown): void;
  leave(consented: boolean): Promise<number>;
  /** Wire `actors` add/change/remove (existing actors are reported immediately). */
  bindActors(handlers: ActorHandlers): () => void;
  /**
   * Wire `vehicles` add/change/remove (existing cars are reported immediately). Interior rooms and
   * servers without cars simply never report any.
   */
  bindVehicles(handlers: VehicleHandlers): () => void;
  /** Listen to a root state field such as `presentationVersion`. */
  listenRoomField(field: string, cb: (value: unknown) => void): () => void;
}

export interface Connector {
  joinWithTicket(roomName: string, ticket: string, joinOptions: Record<string, string>): Promise<RoomHandle>;
  reconnect(reconnectionToken: string): Promise<RoomHandle>;
}

type AnyRoom = Room<unknown, unknown>;

interface LooseCallbacks {
  onAdd<T>(property: string, handler: (value: T, key: string) => void): () => void;
  onRemove<T>(property: string, handler: (value: T, key: string) => void): () => void;
  onChange(instance: object, handler: () => void): () => void;
  listen(property: string, handler: (value: unknown, previous: unknown) => void): () => void;
}

/**
 * The SDK's automatic reconnection after a dropped connection (founder 2026-10-28: dropped inside their home; the
 * retry that would have restored the session arrived a second after the server's 30 s grace). Retries at most 2 s
 * apart (SDK default 5 s), so a connection that comes back is used within ~2 s; 30 tries cover about a minute.
 */
export const RECONNECT_MAX_DELAY_MS = 2000;
export const RECONNECT_MAX_RETRIES = 30;

function wrapRoom(room: AnyRoom): RoomHandle {
  room.reconnection.maxDelay = RECONNECT_MAX_DELAY_MS;
  room.reconnection.maxRetries = RECONNECT_MAX_RETRIES;
  // The SDK types the state callbacks against a concrete schema class; the web client only knows
  // the public field names (the server owns the schema), so the proxy is used through a loose shape.
  const callbacks = () => Callbacks.get(room as never) as unknown as LooseCallbacks;
  const loose = room as unknown as {
    onMessage(type: string, cb: (payload: unknown) => void): () => void;
    send(type: string, payload?: unknown): void;
  };
  return {
    get roomId() {
      return room.roomId;
    },
    get sessionId() {
      return room.sessionId;
    },
    get reconnectionToken() {
      return room.reconnectionToken;
    },
    onMessage: (type, cb) => loose.onMessage(type, cb),
    onLeave: (cb) => {
      room.onLeave(cb);
      return () => room.onLeave.remove(cb);
    },
    onError: (cb) => {
      room.onError(cb);
      return () => room.onError.remove(cb);
    },
    onDrop: (cb) => {
      room.onDrop(cb);
      return () => room.onDrop.remove(cb);
    },
    onReconnect: (cb) => {
      room.onReconnect(cb);
      return () => room.onReconnect.remove(cb);
    },
    send: (type, payload) => loose.send(type, payload),
    leave: (consented) => room.leave(consented),
    bindActors: (handlers) => {
      const $ = callbacks();
      const detachBySession = new Map<string, () => void>();
      const offAdd = $.onAdd<ActorStateLike>("actors", (actor, sessionId) => {
        handlers.onAdd(actor, sessionId);
        detachBySession.get(sessionId)?.();
        detachBySession.set(
          sessionId,
          $.onChange(actor, () => handlers.onChange(actor, sessionId)),
        );
      });
      const offRemove = $.onRemove<ActorStateLike>("actors", (actor, sessionId) => {
        detachBySession.get(sessionId)?.();
        detachBySession.delete(sessionId);
        handlers.onRemove(actor, sessionId);
      });
      return () => {
        offAdd();
        offRemove();
        for (const d of detachBySession.values()) d();
        detachBySession.clear();
      };
    },
    bindVehicles: (handlers) => {
      const $ = callbacks();
      const detachByKey = new Map<string, () => void>();
      let offAdd: () => void = () => {};
      let offRemove: () => void = () => {};
      try {
        // A state without `vehicles` (older server) never fires: the SDK waits for the collection.
        offAdd = $.onAdd<VehicleStateLike>("vehicles", (vehicle, key) => {
          handlers.onAdd(vehicle, key);
          detachByKey.get(key)?.();
          detachByKey.set(
            key,
            $.onChange(vehicle, () => handlers.onChange(vehicle, key)),
          );
        });
        offRemove = $.onRemove<VehicleStateLike>("vehicles", (vehicle, key) => {
          detachByKey.get(key)?.();
          detachByKey.delete(key);
          handlers.onRemove(vehicle, key);
        });
      } catch {
        /* no vehicles collection in this room */
      }
      return () => {
        offAdd();
        offRemove();
        for (const d of detachByKey.values()) d();
        detachByKey.clear();
      };
    },
    listenRoomField: (field, cb) => callbacks().listen(field, (value) => cb(value)),
  };
}

/**
 * The browser always talks to the same origin; `endpoint` is `origin + "/realtime"` (ADR-0002).
 * Each join sets the single-use room ticket as the SDK auth token, which the SDK sends as
 * `Authorization: Bearer` on matchmaking and `_authToken` on the WebSocket upgrade.
 */
export function createSdkConnector(endpoint: string): Connector {
  const client = new Client(endpoint);
  return {
    async joinWithTicket(roomName, ticket, joinOptions) {
      client.auth.token = ticket;
      const room = await client.joinOrCreate(roomName, joinOptions);
      return wrapRoom(room as AnyRoom);
    },
    async reconnect(reconnectionToken) {
      const room = await client.reconnect(reconnectionToken);
      return wrapRoom(room as AnyRoom);
    },
  };
}
